import React from 'react';
import { Typography, Grid, Button, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import LocalVideoPreview from './LocalVideoPreview/LocalVideoPreview';
import SettingsMenu from './SettingsMenu/SettingsMenu';
import { Steps } from '../PreJoinScreens';
import ToggleAudioButton from '../../Buttons/ToggleAudioButton/ToggleAudioButton';
import ToggleVideoButton from '../../Buttons/ToggleVideoButton/ToggleVideoButton';
import { useAppState } from '../../../state';
import useChatContext from '../../../hooks/useChatContext/useChatContext';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';
import useVoiceContext from '../../../hooks/useVoiceContext/useVoiceContext';

import { runPreflight } from 'twilio-video';

import { maskIdentity } from '../../../utils';

const GutterBottomTypography = styled(Typography)({
  marginBottom: '1em',
});

const CaptionFont = styled(Typography)({
  textAlign: 'center',
  marginBottom: '1.75em',
  color: 'red',
});

const LocalPreviewContainer = styled('div')(({ theme }) => ({
  paddingRight: '2em',
  [theme.breakpoints.down('sm')]: {
    padding: '0 2.5em',
  },
}));

const JoinButtons = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column-reverse',
    width: '100%',
    '& button': {
      margin: '0.5em 0',
    },
  },
}));

const MobileButtonBar = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    justifyContent: 'center',
    margin: '1.5em 0 1em',
  },
}));

const StyledToggleAudioButton = styled(ToggleAudioButton)({
  '& .micIcon *': {
    fill: '#10416D',
  },
  justifyContent: 'flex-start',
});

const StyledToggleVideoButton = styled(ToggleVideoButton)({
  '& .videoOnIcon *': {
    fill: '#10416D',
  },
  justifyContent: 'flex-start',
});

const MobileToggleAudioButton = styled(ToggleAudioButton)({
  padding: '0.8em 0',
  margin: 0,
  '& .micIcon *': {
    fill: '#10416D',
  },
});

const MobileToggleVideoButton = styled(ToggleVideoButton)({
  padding: '0.8em 0',
  margin: 0,
  '& .videoOnIcon *': {
    fill: '#10416D',
  },
});

const MobileSettingsMenu = styled(SettingsMenu)({
  padding: '0.8em 0',
  margin: 0,
  '& .micIcon *': {
    fill: '#10416D',
  },
  '& .videoOnIcon *': {
    fill: '#10416D',
  },
});

function fetchConnectedParticipants(meetingId: string) {
  const api_key = window.sessionStorage.getItem('m_key');
  const m_key = api_key ? api_key : '';

  return fetch(`${process.env.REACT_APP_API_BASE_URL}/internal/room/${meetingId}/connected-participants`, {
    method: 'GET',
    headers: {
      'content-type': 'application/json',
      'X-API-KEY': m_key,
    },
  });
}

function saveTestResults(meeting_id: string, key: string, test_data: any) {
  const api_key = window.sessionStorage.getItem('m_key');
  const m_key = api_key ? api_key : '';

  return fetch(`${process.env.REACT_APP_API_BASE_URL}/sync/flightData`, {
    method: 'POST',
    body: JSON.stringify({
      meeting_id,
      key,
      test_data: JSON.stringify(test_data),
    }),
    headers: {
      'content-type': 'application/json',
      'X-API-KEY': m_key,
    },
  });
}

interface DeviceSelectionScreenProps {
  name: string;
  roomName: string;
  topicName: string;
  setStep: (step: Steps) => void;
}

export default function DeviceSelectionScreen({ name, roomName, setStep, topicName }: DeviceSelectionScreenProps) {
  const { getToken, isFetching, meeting } = useAppState();
  const { connect: chatConnect } = useChatContext();
  const { connect: videoConnect, isAcquiringLocalTracks, isConnecting } = useVideoContext();
  const { connect: voiceConnect } = useVoiceContext();
  const disableButtons = isFetching || isAcquiringLocalTracks || isConnecting;

  const handleJoin = async () => {
    const participantName = maskIdentity(name, meeting);
    localStorage.setItem('userIdentity', participantName);

    function runPreFlightTest(token: string) {
      const preflightTest = runPreflight(token);
      preflightTest.on('progress', progress => {
        console.log('preflight progress:', progress);
      });

      preflightTest.on('failed', (error, report) => {
        console.log('Report ' + JSON.stringify(report));
        const result = { ...report, isSuccess: false, errorDetails: error };
        saveTestResults(roomName, participantName, result);
      });

      preflightTest.on('completed', report => {
        console.log('Report ' + JSON.stringify(report));
        const result = { ...report, isSuccess: true };
        saveTestResults(roomName, participantName, result);
      });
    }

    getToken(participantName, roomName).then(({ token, voice_token }) => {
      runPreFlightTest(token);
      videoConnect(token);
      process.env.REACT_APP_DISABLE_TWILIO_CONVERSATIONS !== 'true' && chatConnect(token);
      voiceConnect(voice_token);
    });
  };

  if (isFetching || isConnecting) {
    return (
      <Grid container justifyContent="center" alignItems="center" direction="column" style={{ height: '100%' }}>
        <div>
          <CircularProgress variant="indeterminate" />
        </div>
        <div>
          <Typography variant="body2" style={{ fontWeight: 'bold', fontSize: '16px' }}>
            Joining Meeting
          </Typography>
        </div>
      </Grid>
    );
  }

  return (
    <>
      <GutterBottomTypography variant="h5">
        Join {topicName}
      </GutterBottomTypography>

      {meeting?.is_anonymous || Boolean(meeting?.isChaperoning) ? (
        <CaptionFont variant="subtitle2">
          You are about to join an <strong>anonymous</strong> meeting
        </CaptionFont>
      ) : (
        <CaptionFont variant="subtitle2">
          You are about to join a <strong>disclosed</strong> meeting
        </CaptionFont>
      )}

      <Grid container justifyContent="center">
        <Grid size={{ xs: 12, sm: 12, md: 7 }}>
          <LocalPreviewContainer>
            <LocalVideoPreview identity={maskIdentity(name, meeting)} />
          </LocalPreviewContainer>
          <MobileButtonBar>
            <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
              <MobileToggleAudioButton disabled={disableButtons} />
              {meeting?.is_anonymous === false && (
                <MobileToggleVideoButton disabled={disableButtons} />
              )}
              <MobileSettingsMenu />
            </Box>
          </MobileButtonBar>
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 5 }}>
          <Grid container direction="column" justifyContent="space-between" style={{ height: '100%' }}>
            <div>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <StyledToggleAudioButton
                  disabled={disableButtons || (meeting && Boolean(meeting.isChaperoning))}
                />
                {meeting?.is_anonymous === false && (
                  <StyledToggleVideoButton disabled={disableButtons} />
                )}
                <MobileSettingsMenu />
              </Box>
            </div>
            <JoinButtons>
              <Button variant="outlined" color="primary" onClick={() => setStep(Steps.roomNameStep)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                data-cy-join-now
                onClick={handleJoin}
                disabled={disableButtons}
                style={disableButtons?{}:{ color: 'white' }}
              >
                Join Now
              </Button>
            </JoinButtons>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}
